import React, { useEffect, useState, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, FileBarChart, Check, X, Package, PenLine, Download, RotateCcw, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { generateReportPDF } from '../lib/pdf';
import RowActions from '../components/RowActions';
import BuildingFilter from '../components/BuildingFilter';

const APPROVE_ROLES = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'CS_SUPERVISOR', 'ENGINEER'];
const ISSUE_ROLES = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'STOREKEEPER', 'TECHNICIAN'];
const RETURN_ROLES = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'STOREKEEPER', 'TECHNICIAN'];
const REPORT_SIGNOFF_ROLES = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER'];
// Roles allowed to edit *any* report belonging to their company (until it's approved/closed).
// Author (technician) can always edit their own report — that check is done inline.
const ENGINEER_EDIT_ROLES = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER'];

// Phase 38 — friendly SR-document-lifecycle labels (the linked complaint has
// its own independent status shown on the Complaints page — they are two
// different lifecycles: SR = document; Complaint = tenant-facing progress).
const REPORT_STATUS_LABEL = {
  materials_pending:  '⏳ Materials — awaiting engineer approval',
  materials_approved: '✓ Materials approved — waiting for storekeeper to issue',
  materials_issued:   '📦 Materials issued',
  signed:             '✍️ Signed by tenant',
  approved:           '✅ Verified & closed by engineer',
  rejected:           '⛔ Rejected — rework needed',
  completed:          '📝 Completed — awaiting engineer verification',
};

/** Derive a stable material request number: MRQ-<ticket>-<idx01> */
function materialRequestNo(ticket, idx) {
  if (!ticket) return `MRQ-${(idx + 1).toString().padStart(2, '0')}`;
  return `MRQ-${ticket}-${(idx + 1).toString().padStart(2, '0')}`;
}

export default function ServiceReports() {
  const { user } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [company, setCompany] = useState(null);
  const [tab, setTab] = useState('service');
  // Feb-2026 UX fixes:
  //  1. `statusTab` — Active | Closed sub-toggle so the SR summary isn't
  //     mixed up with historical closed/approved reports.
  //  2. Rich filter bar: text search (ticket / summary / description),
  //     date range, and technician selector.
  const [statusTab, setStatusTab] = useState('active');
  const [searchQ, setSearchQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterTech, setFilterTech] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [signFor, setSignFor] = useState(null);
  const [forceCloseFor, setForceCloseFor] = useState(null);
  const [forceCloseReason, setForceCloseReason] = useState('');
  const [forceCloseMethod, setForceCloseMethod] = useState('phone');
  const [signature, setSignature] = useState('');
  // Phase 38 — engineer/manager clicks "Approve Materials" → dialog opens where
  // they toggle Returnable / Consumable per line before submitting the decision.
  const [approveDialogFor, setApproveDialogFor] = useState(null);
  const [returnableChoices, setReturnableChoices] = useState({}); // { [idx]: bool }
  const [filterBuildingId, setFilterBuildingId] = useState('all');
  // Edit / view state for a report row (Bug fix Feb-17)
  const [editingId, setEditingId] = useState(null);
  const [viewing, setViewing] = useState(null);

  const [newR, setNewR] = useState({
    building_id: '', report_type: 'service', complaint_id: '', work_order_id: '',
    summary: '', details: '', time_spent: 0,
    materials_used: [], materials_needed: [],
    complaint_status_recommendation: '',
    // Phase 7
    report_source: 'in_house',  // in_house | service_provider
    service_provider_id: '',
    amc_contract_id: '',
    amc_scope_item_id: '',
    sla_target_hours: '',
    sla_actual_hours: ''
  });
  const [providers, setProviders] = useState([]);
  const [amcContracts, setAmcContracts] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);

  // material form (Phase 38: technicians cannot decide returnable — engineer sets at approval)
  const [matForm, setMatForm] = useState({ item: '', qty: 1, unit: 'pcs', brand: '', size: '', stock_item_id: '' });

  useEffect(() => { fetchData(); }, [tab]);

  // Phase 38 — deep-link: /reports?new=1&complaint_id=<id> auto-opens the
  // New-Report dialog pre-linked to the complaint (technician clicks "Request
  // Materials" from the complaint page and lands here ready to fill).
  //
  // Feb-2026 workflow polish — /reports?focus=<report_id> lets Approvals /
  // notifications deep-link straight to a specific report: switches to the
  // correct tab, scrolls the row into view, briefly ring-highlights it, and
  // auto-opens the View dialog so the engineer can Verify / Force-Close in
  // one click without hunting the list.
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightId, setHighlightId] = useState(null);
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      const cid = searchParams.get('complaint_id') || '';
      setNewR(prev => ({ ...prev, complaint_id: cid }));
      setShowNew(true);
      searchParams.delete('new');
      searchParams.delete('complaint_id');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // Focus-on-a-specific-report deep link. Runs after each fetch so the tab
  // switch happens before we try to scroll.
  useEffect(() => {
    const focusId = searchParams.get('focus');
    if (!focusId || loading) return;
    const target = items.find(x => x.id === focusId);
    if (!target) return;
    // Ensure we're on the tab that actually contains this report.
    if (target.report_type && tab !== target.report_type) {
      setTab(target.report_type);
      return; // useEffect will re-run after tab change triggers refetch.
    }
    // Scroll + highlight + auto-open view
    setHighlightId(focusId);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="report-card-${focusId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setViewing(target);
    }, 150);
    // Clear the ?focus after 4s so future refreshes don't re-trigger.
    const t = setTimeout(() => {
      setHighlightId(null);
      searchParams.delete('focus');
      setSearchParams(searchParams, { replace: true });
    }, 4000);
    return () => clearTimeout(t);
  }, [loading, items, tab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [r, b, c, s, comp, pv, ac, wo] = await Promise.all([
        axios.get(`${API}/service-reports?report_type=${tab}`, { withCredentials: true }),
        axios.get(`${API}/buildings`, { withCredentials: true }),
        axios.get(`${API}/complaints`, { withCredentials: true }),
        axios.get(`${API}/stock-items`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/companies`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/suppliers`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/amc-contracts`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/work-orders`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setItems(r.data); setBuildings(b.data); setComplaints(c.data); setStockItems(s.data);
      setCompany(comp.data.find(x => x.id === user?.company_id) || comp.data[0]);
      setProviders(pv.data); setAmcContracts(ac.data);
      setWorkOrders((wo.data || []).filter(w => w.status !== 'closed' && w.status !== 'cancelled'));
    } catch { toast.error('Failed'); }
    setLoading(false);
  };

  const addMaterial = (target) => {
    if (!matForm.item) return;
    const m = { ...matForm, qty: parseFloat(matForm.qty) || 1 };
    setNewR(prev => ({ ...prev, [target]: [...prev[target], m] }));
    setMatForm({ item: '', qty: 1, unit: 'pcs', brand: '', size: '', stock_item_id: '' });
  };

  const removeMaterial = (target, i) => {
    setNewR(prev => ({ ...prev, [target]: prev[target].filter((_, idx) => idx !== i) }));
  };

  const handleCreate = async () => {
    try {
      // 🐛 FIX: If the technician typed material info but forgot to click
      // "+ Request", auto-commit it now so we don't silently drop the
      // material request (root cause of the "no MR in approval queue" bug).
      let pending = { ...newR };
      if (matForm.item && matForm.item.trim()) {
        const m = { ...matForm, qty: parseFloat(matForm.qty) || 1 };
        pending = { ...pending, materials_needed: [...pending.materials_needed, m] };
      }
      const payload = {
        ...pending, report_type: tab,
        time_spent: parseFloat(pending.time_spent) || 0,
        complaint_id: pending.complaint_id || null,
        work_order_id: pending.work_order_id || null,
        complaint_status_recommendation: pending.complaint_status_recommendation || null,
        service_provider_id: pending.report_source === 'service_provider' ? (pending.service_provider_id || null) : null,
        amc_contract_id: pending.amc_contract_id || null,
        amc_scope_item_id: pending.amc_scope_item_id || null,
        sla_target_hours: pending.sla_target_hours ? parseFloat(pending.sla_target_hours) : null,
        sla_actual_hours: pending.sla_actual_hours ? parseFloat(pending.sla_actual_hours) : null
      };
      if (editingId) {
        // Edit mode — PUT to the new /service-reports/{id} endpoint.
        await axios.put(`${API}/service-reports/${editingId}`, payload, { withCredentials: true });
        toast.success('Report updated' + (payload.materials_needed?.length ? ' · material request notified' : ''));
      } else {
        await axios.post(`${API}/service-reports`, payload, { withCredentials: true });
        toast.success('Report submitted' + (payload.materials_needed.length ? ' · awaiting material approval' : ''));
      }
      setShowNew(false);
      setEditingId(null);
      setMatForm({ item: '', qty: 1, unit: 'pcs', brand: '', size: '', stock_item_id: '' });
      setNewR({
        building_id: '', report_type: 'service', complaint_id: '', work_order_id: '', summary: '', details: '', time_spent: 0,
        materials_used: [], materials_needed: [], complaint_status_recommendation: '',
        report_source: 'in_house', service_provider_id: '', amc_contract_id: '', amc_scope_item_id: '',
        sla_target_hours: '', sla_actual_hours: ''
      });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Edit an existing report — pre-fill the create dialog with its values.
  const openEditReport = (r) => {
    setEditingId(r.id);
    setNewR({
      building_id: r.building_id || '',
      report_type: r.report_type || 'service',
      complaint_id: r.complaint_id || '',
      work_order_id: r.work_order_id || '',
      summary: r.summary || '',
      details: r.details || '',
      time_spent: r.time_spent || 0,
      materials_used: Array.isArray(r.materials_used) ? [...r.materials_used] : [],
      materials_needed: Array.isArray(r.materials_needed) ? [...r.materials_needed] : [],
      complaint_status_recommendation: r.complaint_status_recommendation || '',
      report_source: r.report_source || 'in_house',
      service_provider_id: r.service_provider_id || '',
      amc_contract_id: r.amc_contract_id || '',
      amc_scope_item_id: r.amc_scope_item_id || '',
      sla_target_hours: r.sla_target_hours ?? '',
      sla_actual_hours: r.sla_actual_hours ?? '',
    });
    setShowNew(true);
  };

  const approveMat = async (id, returnableOverrides = {}) => {
    try {
      await axios.put(`${API}/service-reports/${id}/approve-materials`, { returnable_overrides: returnableOverrides }, { withCredentials: true });
      toast.success('Materials approved · storekeeper notified');
      setApproveDialogFor(null);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const rejectMat = async (id) => {
    const notes = window.prompt('Rejection reason (shared with the technician):', '');
    if (notes === null) return; // cancelled
    try {
      await axios.put(`${API}/service-reports/${id}/reject-materials`, { notes: notes.trim() }, { withCredentials: true });
      toast.info('Materials rejected' + (notes.trim() ? ` · reason recorded` : ''));
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const approveReport = async (id) => {
    if (!window.confirm('Approve this service report? The linked complaint will be closed.')) return;
    try {
      const r = await axios.put(`${API}/service-reports/${id}/approve`, {}, { withCredentials: true });
      toast.success(r.data?.complaint_closed ? 'Report approved · complaint closed' : 'Report approved');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const rejectReport = async (id) => {
    const notes = window.prompt('Rejection reason for the technician:', '');
    if (notes === null) return;
    try {
      await axios.put(`${API}/service-reports/${id}/reject`, { notes: notes.trim() }, { withCredentials: true });
      toast.info('Report rejected · technician notified');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const issueMat = async (id, backorderIndices = null) => {
    try {
      const body = backorderIndices ? { backorder_indices: backorderIndices } : {};
      const r = await axios.put(`${API}/service-reports/${id}/issue-materials`, body, { withCredentials: true });
      const bo = (r.data.backordered || []).length;
      toast.success(`Issued ${r.data.issued} item(s)${bo ? ` · ${bo} backordered (PR raised)` : ''} · stock updated`);
      fetchData();
    } catch (e) {
      // Phase 39a — Backend returns 409 with { out_of_stock:[{index,item,requested,available}] }
      // when stock is short and caller didn't flag those lines for backorder.
      const d = e.response?.data?.detail;
      const oos = (typeof d === 'object' && d?.out_of_stock) || null;
      if (oos && oos.length) {
        const summary = oos.map(x => `• ${x.item}: requested ${x.requested}, only ${x.available} in stock`).join('\n');
        const proceed = window.confirm(
          `Some items are out of stock:\n\n${summary}\n\nRaise a Purchase Request for the shortfall AND issue the rest now?\n\nOK = raise PR + issue available · Cancel = do nothing.`
        );
        if (proceed) {
          const indices = oos.map(x => x.index);
          issueMat(id, indices);
        }
        return;
      }
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const returnMat = async (reportId, index) => {
    const cond = window.prompt('Return condition — type: good, damaged, or lost', 'good');
    if (cond === null) return;
    const condition = (cond || 'good').toLowerCase().trim();
    if (!['good', 'damaged', 'lost'].includes(condition)) {
      toast.error('Condition must be good, damaged, or lost');
      return;
    }
    const notes = window.prompt('Return notes (optional):', '') || '';
    try {
      const r = await axios.put(`${API}/service-reports/${reportId}/return-material`, { index, condition, notes: notes.trim() }, { withCredentials: true });
      if (r.data.stock_restored) toast.success(`Item returned · stock restored (+${index === 0 ? '' : ''})`);
      else toast.success(`Item returned as ${condition}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to mark return'); }
  };

  const toggleReturnableInRequest = (target, idx) => {
    setNewR(prev => ({
      ...prev,
      [target]: prev[target].map((m, i) => i === idx ? { ...m, is_returnable: !m.is_returnable } : m),
    }));
  };

  const sign = async () => {
    if (!signature) { toast.error('Please sign first'); return; }
    try {
      await axios.put(`${API}/service-reports/${signFor.id}/sign`, { signature }, { withCredentials: true });
      toast.success('Report signed · complaint closed');
      setSignFor(null); setSignature('');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const downloadReportPDF = (rep) => {
    const b = buildings.find(x => x.id === rep.building_id);
    const comp = complaints.find(c => c.id === rep.complaint_id);
    generateReportPDF({
      title: `${tab === 'service' ? 'Service' : 'Incident'} Report`,
      subtitle: `${rep.summary} · ${new Date(rep.created_at).toLocaleDateString()}`,
      company,
      sections: [
        {
          heading: 'Details',
          head: ['Field', 'Value'],
          rows: [
            ['Building', b?.name || '-'],
            ['Complaint', comp?.title || '-'],
            ['Technician', rep.technician_name || '-'],
            ['Hours', rep.time_spent || 0],
            ['Status', rep.status]
          ]
        },
        rep.details ? { heading: 'Findings', paragraph: rep.details } : null,
        rep.materials_used?.length ? {
          heading: 'Materials Used',
          head: ['Item', 'Qty', 'Unit'],
          rows: rep.materials_used.map(m => [m.item, m.qty, m.unit || '-'])
        } : null,
        rep.materials_needed?.length ? {
          heading: 'Materials Requested',
          head: ['Item', 'Qty', 'Unit', 'Status'],
          rows: rep.materials_needed.map(m => [m.item, m.qty, m.unit || '-', m.status || 'pending'])
        } : null,
        rep.tenant_signature ? {
          heading: 'Tenant Signature',
          paragraph: `Signed by: ${rep.tenant_signed_by} on ${new Date(rep.tenant_signed_at).toLocaleString()}\nSignature: ${rep.tenant_signature.startsWith('data:') ? '[image signature attached]' : rep.tenant_signature}`
        } : null
      ].filter(Boolean)
    });
  };

  const downloadAllPDF = () => {
    generateReportPDF({
      title: tab === 'service' ? 'Service Reports' : 'Incident Reports',
      subtitle: `${items.length} records`,
      company,
      sections: [{
        heading: 'Reports',
        head: ['Date', 'Building', 'Technician', 'Summary', 'Materials', 'Status', 'Hours'],
        rows: items.slice(0, 50).map(r => [
          new Date(r.created_at).toLocaleDateString(),
          buildings.find(b => b.id === r.building_id)?.name || '-',
          r.technician_name || '-',
          (r.summary || '').slice(0, 50),
          `${r.materials_used?.length || 0} used / ${r.materials_needed?.length || 0} req`,
          r.status,
          r.time_spent || 0
        ])
      }]
    });
  };

  const matStatusBadge = (s) => ({
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-blue-100 text-blue-800',
    issued: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800'
  }[s] || 'bg-slate-100 text-slate-800');

  return (
    <Layout>
      <div data-testid="reports-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Service & Incident Reports</h1>
            <p className="text-sm text-muted-foreground">Field reports with material workflow and tenant signature</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="download-reports-pdf" onClick={downloadAllPDF} variant="outline"><Download className="w-4 h-4 mr-1" />PDF</Button>
            <Dialog open={showNew} onOpenChange={(o) => { setShowNew(o); if (!o) setEditingId(null); }}>
              <DialogTrigger asChild><Button data-testid="new-report-btn" className="bg-teal-600 hover:bg-teal-700"><Plus className="w-4 h-4 mr-1" />New {tab === 'service' ? 'Service' : 'Incident'} Report</Button></DialogTrigger>
              <DialogContent className="w-[98vw] sm:w-[95vw] max-w-6xl max-h-[95vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{editingId ? 'Edit' : 'Submit'} {tab === 'service' ? 'Service' : 'Incident'} Report</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  {tab === 'service' && (
                    <div className="border-2 border-teal-200 rounded-lg p-3 bg-teal-50/50">
                      <Label className="flex items-center gap-1">
                        <span className="text-teal-700 font-semibold">Work Order</span>
                        <span className="text-rose-600">*</span>
                      </Label>
                      <p className="text-[11px] text-slate-500 mb-2">
                        A service report must be linked to a Work Order. If you don&apos;t see the WO, ask your Engineer to raise one from the complaint first.
                      </p>
                      <Select value={newR.work_order_id} onValueChange={(v) => {
                        const w = workOrders.find(x => x.id === v);
                        setNewR({
                          ...newR,
                          work_order_id: v,
                          building_id: newR.building_id || w?.building_id || '',
                          complaint_id: newR.complaint_id || (w?.source === 'complaint' ? w.source_id : '') || ''
                        });
                      }}>
                        <SelectTrigger data-testid="report-work-order"><SelectValue placeholder="Pick Work Order…" /></SelectTrigger>
                        <SelectContent>
                          {workOrders.length === 0 && (
                            <div className="px-3 py-2 text-xs text-slate-500">No open Work Orders. <a href="/work-orders" className="text-teal-700 underline">Go to Work Orders</a></div>
                          )}
                          {workOrders.map(w => (
                            <SelectItem key={w.id} value={w.id}>
                              {w.wo_number} — {(w.title || '').slice(0, 45)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Building</Label>
                      <Select value={newR.building_id} onValueChange={(v) => setNewR({ ...newR, building_id: v })}>
                        <SelectTrigger data-testid="report-building"><SelectValue placeholder="Building" /></SelectTrigger>
                        <SelectContent>{buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Linked Complaint (opt)</Label>
                      <Select value={newR.complaint_id} onValueChange={(v) => setNewR({ ...newR, complaint_id: v })}>
                        <SelectTrigger data-testid="report-complaint"><SelectValue placeholder="None" /></SelectTrigger>
                        <SelectContent>{complaints.filter(c => c.status !== 'closed').map(c => <SelectItem key={c.id} value={c.id}>{c.title.slice(0, 50)}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label>Summary</Label><Input data-testid="report-summary" value={newR.summary} onChange={(e) => setNewR({ ...newR, summary: e.target.value })} placeholder="Brief summary" /></div>
                  <div><Label>Findings / Details</Label><Textarea rows={4} value={newR.details} onChange={(e) => setNewR({ ...newR, details: e.target.value })} /></div>

                  {/* Phase 7: Report Source + AMC linkage */}
                  <div className="border rounded-lg p-3 bg-teal-50/50 space-y-3">
                    <Label className="text-xs uppercase tracking-wide block">Source &amp; AMC Linkage</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Report Source</Label>
                        <Select value={newR.report_source} onValueChange={(v) => setNewR({ ...newR, report_source: v, service_provider_id: v === 'in_house' ? '' : newR.service_provider_id })}>
                          <SelectTrigger data-testid="report-source-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="in_house">In-House Team</SelectItem>
                            <SelectItem value="service_provider">Service Provider (AMC)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {newR.report_source === 'service_provider' && (
                        <div>
                          <Label className="text-xs">Service Provider</Label>
                          <Select value={newR.service_provider_id} onValueChange={(v) => setNewR({ ...newR, service_provider_id: v })}>
                            <SelectTrigger data-testid="report-provider-select"><SelectValue placeholder="Select provider" /></SelectTrigger>
                            <SelectContent>{providers.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">AMC Contract (optional)</Label>
                        <Select value={newR.amc_contract_id || 'none'} onValueChange={(v) => setNewR({ ...newR, amc_contract_id: v === 'none' ? '' : v, amc_scope_item_id: '' })}>
                          <SelectTrigger data-testid="report-amc-select"><SelectValue placeholder="None" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {amcContracts
                              .filter(c => !newR.service_provider_id || c.service_provider_id === newR.service_provider_id)
                              .map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      {newR.amc_contract_id && (
                        <div>
                          <Label className="text-xs">Scope Item</Label>
                          <Select value={newR.amc_scope_item_id || 'none'} onValueChange={(v) => setNewR({ ...newR, amc_scope_item_id: v === 'none' ? '' : v })}>
                            <SelectTrigger><SelectValue placeholder="Select scope item" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">—</SelectItem>
                              {(amcContracts.find(c => c.id === newR.amc_contract_id)?.scope_items || [])
                                .map(s => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">SLA Target Hours</Label>
                        <Input type="number" data-testid="sla-target-input" placeholder="e.g. 24" value={newR.sla_target_hours} onChange={(e) => setNewR({ ...newR, sla_target_hours: e.target.value })} />
                      </div>
                      <div>
                        <Label className="text-xs">SLA Actual Hours</Label>
                        <Input type="number" data-testid="sla-actual-input" placeholder="e.g. 18.5" value={newR.sla_actual_hours} onChange={(e) => setNewR({ ...newR, sla_actual_hours: e.target.value })} />
                      </div>
                    </div>
                    {newR.sla_target_hours && newR.sla_actual_hours && (
                      <div className={`text-xs font-semibold ${parseFloat(newR.sla_actual_hours) <= parseFloat(newR.sla_target_hours) ? 'text-emerald-700' : 'text-red-700'}`}>
                        {parseFloat(newR.sla_actual_hours) <= parseFloat(newR.sla_target_hours) ? '✓ SLA met' : '✗ SLA breached'}
                      </div>
                    )}
                  </div>

                  {newR.complaint_id && (
                    <div><Label>Complaint Status After Work</Label>
                      <Select value={newR.complaint_status_recommendation} onValueChange={(v) => setNewR({ ...newR, complaint_status_recommendation: v })}>
                        <SelectTrigger><SelectValue placeholder="Don't change" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="in_progress">Still In Progress</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Materials Used */}
                  <div className="border rounded-lg p-3 bg-slate-50">
                    <Label className="text-xs uppercase tracking-wide mb-2 block">Materials Used (already consumed)</Label>
                    <div className="grid grid-cols-12 gap-2 mb-2">
                      <Input data-testid="mat-used-item" className="col-span-5" placeholder="Item" value={matForm.item} onChange={(e) => setMatForm({ ...matForm, item: e.target.value })} />
                      <Input className="col-span-2" type="number" placeholder="Qty" value={matForm.qty} onChange={(e) => setMatForm({ ...matForm, qty: e.target.value })} />
                      <Input className="col-span-2" placeholder="Unit" value={matForm.unit} onChange={(e) => setMatForm({ ...matForm, unit: e.target.value })} />
                      <Button data-testid="add-mat-used-btn" type="button" size="sm" className="col-span-3 bg-slate-700 hover:bg-slate-800" onClick={() => addMaterial('materials_used')}>+ Add Used</Button>
                    </div>
                    {newR.materials_used.map((m, i) => (
                      <div key={i} className="flex justify-between items-center text-sm py-1">
                        <span>{m.item} — {m.qty} {m.unit}</span>
                        <button onClick={() => removeMaterial('materials_used', i)} className="text-red-500"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>

                  {/* Materials Needed block moved to Work Order → Material Request panel.
                      A Service Report is a POST-service document; requests happen before the work. */}
                  <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 text-xs text-slate-600">
                    <div className="font-semibold text-slate-700 mb-1 flex items-center gap-1"><Package className="w-3 h-3" />Need material?</div>
                    Raise the request from the linked Work Order → &ldquo;Request material from store&rdquo; panel.
                    Storekeeper approvals, stock issues and auto-PR shortfalls all live on the <a href="/material-requests" className="text-purple-600 hover:underline">Material Requests queue</a>.
                  </div>

                  <div><Label>Hours spent</Label><Input type="number" step="0.5" value={newR.time_spent} onChange={(e) => setNewR({ ...newR, time_spent: e.target.value })} /></div>
                  <Button data-testid="submit-report-btn" onClick={handleCreate} disabled={!newR.summary || !newR.building_id || (tab === 'service' && !newR.work_order_id && !editingId)} className="w-full bg-teal-600 hover:bg-teal-700">{editingId ? 'Save Changes' : 'Submit Report'}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="flex gap-2 border-b border-slate-200 items-center flex-wrap">
          {[{ id: 'service', label: 'Service' }, { id: 'incident', label: 'Incident' }].map(t => (
            <button key={t.id} data-testid={`report-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium transition border-b-2 ${tab === t.id ? 'border-teal-600 text-teal-600' : 'border-transparent text-slate-600'}`}>{t.label}</button>
          ))}
          <div className="ml-auto pb-2 flex items-center gap-2">
            {/* Active | Closed sub-toggle */}
            <div className="inline-flex rounded-lg border bg-slate-50 p-0.5 text-xs" data-testid="sr-status-toggle">
              <button
                type="button"
                onClick={() => setStatusTab('active')}
                data-testid="sr-tab-active"
                className={`px-3 py-1 rounded-md font-semibold transition ${statusTab === 'active' ? 'bg-teal-600 text-white shadow' : 'text-slate-600 hover:bg-white'}`}
              >Active</button>
              <button
                type="button"
                onClick={() => setStatusTab('closed')}
                data-testid="sr-tab-closed"
                className={`px-3 py-1 rounded-md font-semibold transition ${statusTab === 'closed' ? 'bg-slate-700 text-white shadow' : 'text-slate-600 hover:bg-white'}`}
              >Closed</button>
            </div>
            <BuildingFilter value={filterBuildingId} onChange={setFilterBuildingId} buildings={buildings} />
          </div>
        </div>

        {/* Filter bar — search, date range, technician */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 pb-1" data-testid="sr-filter-bar">
          <Input
            data-testid="sr-filter-search"
            placeholder="Search ticket, summary, remarks…"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            className="text-sm"
          />
          <Input
            data-testid="sr-filter-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="text-sm"
            aria-label="From date"
          />
          <Input
            data-testid="sr-filter-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="text-sm"
            aria-label="To date"
          />
          <Select value={filterTech} onValueChange={setFilterTech}>
            <SelectTrigger data-testid="sr-filter-technician" className="text-sm"><SelectValue placeholder="Technician" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All technicians</SelectItem>
              {Array.from(new Map(items.filter(r => r.technician_id).map(r => [r.technician_id, r.technician_name || '(unknown)'])).entries())
                .map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {(searchQ || dateFrom || dateTo || filterTech !== 'all' || filterBuildingId !== 'all') && (
          <div className="flex items-center gap-2 text-xs -mt-1">
            <span className="text-slate-500">Filters active</span>
            <button
              type="button"
              onClick={() => { setSearchQ(''); setDateFrom(''); setDateTo(''); setFilterTech('all'); setFilterBuildingId('all'); }}
              data-testid="sr-filter-clear"
              className="text-teal-600 hover:underline font-medium"
            >Clear all</button>
          </div>
        )}

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading…</div> : (
          <div className="grid gap-3">
            {items
              .filter(r => filterBuildingId === 'all' || r.building_id === filterBuildingId)
              // Active | Closed segmentation
              .filter(r => {
                const isClosed = ['approved', 'closed', 'rejected'].includes(r.status);
                return statusTab === 'closed' ? isClosed : !isClosed;
              })
              // Technician filter
              .filter(r => filterTech === 'all' || r.technician_id === filterTech)
              // Date range filter (inclusive)
              .filter(r => {
                if (!dateFrom && !dateTo) return true;
                const created = r.created_at ? new Date(r.created_at) : null;
                if (!created) return false;
                if (dateFrom && created < new Date(dateFrom + 'T00:00:00')) return false;
                if (dateTo && created > new Date(dateTo + 'T23:59:59')) return false;
                return true;
              })
              // Free-text search across ticket, summary, description, details
              .filter(r => {
                if (!searchQ.trim()) return true;
                const q = searchQ.trim().toLowerCase();
                return [
                  r.complaint_ticket_number, r.summary, r.title, r.description, r.details,
                  r.technician_name, r.wo_number,
                ].some(f => (f || '').toString().toLowerCase().includes(q));
              })
              // Feb-2026 — sort by (a) status priority: active first, closed
              // at bottom; (b) within same group, newest on top.
              .slice()
              .sort((a, b) => {
                const closedRank = (s) => (['approved', 'closed', 'rejected'].includes(s) ? 1 : 0);
                const ra = closedRank(a.status);
                const rb = closedRank(b.status);
                if (ra !== rb) return ra - rb;
                return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
              })
              .map(r => {
              const b = buildings.find(x => x.id === r.building_id);
              const comp = complaints.find(c => c.id === r.complaint_id);
              const pendingMats = (r.materials_needed || []).filter(m => m.status === 'pending');
              const approvedMats = (r.materials_needed || []).filter(m => m.status === 'approved');

              return (
                <Card key={r.id} data-testid={`report-card-${r.id}`} className={`p-5 transition-all ${highlightId === r.id ? 'ring-4 ring-amber-400 ring-offset-2 shadow-lg' : ''}`}>
                  <div className="flex justify-between items-start gap-3 mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {(r.complaint_ticket_number || comp?.ticket_number) && (
                          <span data-testid={`report-${r.id}-ticket`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/10 text-amber-700 border border-amber-500/40 text-[11px] font-mono font-semibold">
                            {r.complaint_ticket_number || comp.ticket_number}
                          </span>
                        )}
                        <h3 className="font-semibold text-foreground">{r.summary}</h3>
                        <span
                          data-testid={`report-status-${r.id}`}
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : r.status === 'rejected' ? 'bg-rose-100 text-rose-800' : r.status === 'signed' ? 'bg-green-100 text-green-800' : r.status === 'materials_pending' ? 'bg-yellow-100 text-yellow-800' : r.status === 'materials_approved' ? 'bg-lime-100 text-lime-800' : r.status === 'materials_issued' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'}`}
                          title={r.status || 'draft'}
                        >
                          {REPORT_STATUS_LABEL[r.status] || (r.status?.replace(/_/g, ' ') || 'draft')}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{b?.name} · by {r.technician_name || '-'} · {new Date(r.created_at).toLocaleDateString()} · {r.time_spent || 0}h</p>
                      {comp && <p className="text-xs text-teal-600 mt-1">↳ Complaint: {comp.title}</p>}
                    </div>
                    <RowActions
                      testIdPrefix={`report-${r.id}`}
                      onView={() => setViewing(r)}
                      onEdit={
                        // Feb-2026 workflow rule: only the author-technician
                        // can edit their own draft SR, and only before it has
                        // been submitted for engineer verification (status
                        // must still be 'draft' or 'completed'). Engineers
                        // cannot edit — they Verify / Reject / Force-Close.
                        (r.technician_id === user?.id && ['draft', 'completed'].includes(r.status))
                          ? () => openEditReport(r)
                          : undefined
                      }
                      onDownloadPdf={() => downloadReportPDF(r)}
                    />
                  </div>
                  {r.details && <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{r.details}</p>}

                  {(r.materials_used?.length > 0 || r.materials_needed?.length > 0) && (
                    <div className="mt-3 grid sm:grid-cols-2 gap-3">
                      {r.materials_used?.length > 0 && (
                        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                          <div className="text-xs font-semibold uppercase text-slate-700 mb-1">Materials Used</div>
                          {r.materials_used.map((m, i) => <div key={i} className="text-xs text-slate-600">• {m.item} — {m.qty} {m.unit}</div>)}
                        </div>
                      )}
                      {r.materials_needed?.length > 0 && (
                        <div className="border-2 border-amber-200 rounded-lg p-3 bg-amber-50 space-y-1">
                          <div className="text-xs font-semibold uppercase text-amber-800 mb-1">Materials Requested</div>
                          {r.materials_needed.map((m, i) => {
                            const returnable = !!m.is_returnable;
                            const returned = !!m.returned_at;
                            const canReturn = returnable && !returned && m.status === 'issued' && RETURN_ROLES.includes(user?.role);
                            return (
                              <div key={i} data-testid={`mat-line-${r.id}-${i}`} className="flex flex-wrap items-center gap-2 text-xs py-1 border-t border-amber-100 first:border-t-0">
                                <code
                                  data-testid={`mrq-no-${r.id}-${i}`}
                                  title="Material Request No — auto-generated from complaint ticket"
                                  className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-amber-200"
                                >
                                  {materialRequestNo(r.complaint_ticket_number || comp?.ticket_number, i)}
                                </code>
                                <span className="flex-1 min-w-0">
                                  📦 <strong>{m.item}</strong>
                                  {m.brand ? ` · ${m.brand}` : ''}{m.size ? ` · ${m.size}` : ''} — {m.qty} {m.unit}
                                </span>
                                {m.status === 'pending' ? (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-600 italic">awaiting engineer decision</span>
                                ) : (
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${returnable ? 'bg-cyan-100 text-cyan-800' : 'bg-slate-200 text-slate-700'}`}>
                                    {returnable ? '🔄 Returnable' : '🗑 Consumable'}
                                  </span>
                                )}
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${matStatusBadge(m.status)}`}>{m.status || 'pending'}</span>
                                {returned && (
                                  <span data-testid={`mat-returned-${r.id}-${i}`} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${m.return_condition === 'good' ? 'bg-emerald-100 text-emerald-800' : m.return_condition === 'damaged' ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'}`}>
                                    ↩ returned ({m.return_condition})
                                  </span>
                                )}
                                {canReturn && (
                                  <Button data-testid={`return-mat-${r.id}-${i}`} type="button" size="sm" variant="outline" className="h-6 px-2 border-cyan-400 text-cyan-700 hover:bg-cyan-50" onClick={() => returnMat(r.id, i)}>
                                    <RotateCcw className="w-3 h-3 mr-0.5" /> Mark Returned
                                  </Button>
                                )}
                                {m.rejection_notes && (
                                  <div className="basis-full text-[10px] text-rose-700 italic mt-0.5"><Trash2 className="w-3 h-3 inline mr-0.5" />Rejected: {m.rejection_notes}</div>
                                )}
                              </div>
                            );
                          })}
                          {r.rejection_notes && !r.materials_needed.some(m => m.rejection_notes) && (
                            <div className="mt-1 text-[11px] text-rose-700 italic">Rejection reason: {r.rejection_notes}</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {pendingMats.length > 0 && APPROVE_ROLES.includes(user?.role) && (
                      <>
                        <Button
                          data-testid={`approve-mat-${r.id}`}
                          onClick={() => {
                            // Prime the returnable choices: default consumable (false) for each pending line
                            const init = {};
                            (r.materials_needed || []).forEach((m, i) => {
                              if (m.status === 'pending') init[i] = !!m.is_returnable;
                            });
                            setReturnableChoices(init);
                            setApproveDialogFor(r);
                          }}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          title="Open dialog to decide Returnable / Consumable per item, then approve"
                        >
                          <Check className="w-4 h-4 mr-1" />Review & Approve
                        </Button>
                        <Button data-testid={`reject-mat-${r.id}`} onClick={() => rejectMat(r.id)} size="sm" variant="outline" className="text-red-600 border-red-600" title="Reject the request with a reason"><X className="w-4 h-4 mr-1" />Reject</Button>
                      </>
                    )}
                    {approvedMats.length > 0 && ISSUE_ROLES.includes(user?.role) && (
                      <Button data-testid={`issue-mat-${r.id}`} onClick={() => issueMat(r.id)} size="sm" className="bg-blue-600 hover:bg-blue-700" title="Issue materials from stock (storekeeper)"><Package className="w-4 h-4 mr-1" />Issue from Store</Button>
                    )}
                    {/* Phase 38 — Engineer closure. Gates the button explicitly:
                          1. No pending material requests
                          2. No approved-but-not-issued materials
                          3. All returnable items must be returned
                          4. If complaint has a tenant, tenant signature required
                        Show a title-tooltip when disabled explaining why. */}
                    {(() => {
                      const complaintHasTenant = comp?.tenant_id;
                      const pending  = (r.materials_needed || []).filter(m => m.status === 'pending').length;
                      const notIssued= (r.materials_needed || []).filter(m => m.status === 'approved').length;
                      const notReturned = (r.materials_needed || []).filter(m => m.is_returnable && m.status === 'issued' && !m.returned_at).length;
                      const needSig = complaintHasTenant && !r.tenant_signature;
                      const canClose = pending === 0 && notIssued === 0 && notReturned === 0 && !needSig && !['approved','rejected'].includes(r.status);
                      const reasons = [];
                      if (pending)     reasons.push(`${pending} material request(s) awaiting your decision`);
                      if (notIssued)   reasons.push(`${notIssued} approved item(s) not yet issued by storekeeper`);
                      if (notReturned) reasons.push(`${notReturned} returnable item(s) not returned to storekeeper`);
                      if (needSig)     reasons.push('Tenant signature required');
                      if (!REPORT_SIGNOFF_ROLES.includes(user?.role) || user?.id === r.technician_id) return null;
                      if (['approved','rejected'].includes(r.status)) return null;
                      return (
                        <>
                          <div className="basis-full text-[11px] uppercase tracking-wide text-slate-500 font-semibold mt-1">Engineer sign-off &amp; closure</div>
                          <Button
                            data-testid={`approve-report-${r.id}`}
                            onClick={() => approveReport(r.id)}
                            disabled={!canClose}
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                            title={canClose ? 'Verify the technician\'s work AND sign-off the report — closes the linked complaint automatically.' : `Cannot verify yet: ${reasons.join(' · ')}`}
                          >
                            <Check className="w-4 h-4 mr-1" />Verify &amp; Sign-off{r.complaint_id ? ' (closes complaint)' : ''}
                          </Button>
                          <Button data-testid={`reject-report-${r.id}`} onClick={() => rejectReport(r.id)} size="sm" variant="outline" className="text-rose-700 border-rose-500" title="Reject the report and send back to technician for rework"><X className="w-4 h-4 mr-1" />Reject &amp; Send Back</Button>
                          {r.complaint_id && needSig && (
                            <Button
                              data-testid={`force-close-sr-${r.id}`}
                              size="sm"
                              variant="outline"
                              className="border-amber-400 text-amber-800 hover:bg-amber-50 dark:text-amber-300"
                              title="Tenant unreachable — force-close the linked complaint after verbal confirmation. Bypasses tenant signature."
                              onClick={() => setForceCloseFor(r)}
                            >
                              <AlertTriangle className="w-4 h-4 mr-1" />Force Close (tenant unreachable)
                            </Button>
                          )}
                          {!canClose && (
                            <span data-testid={`report-blocked-${r.id}`} className="basis-full text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                              ⚠ Waiting: {reasons.join(' · ')}
                            </span>
                          )}
                        </>
                      );
                    })()}
                    {r.status === 'approved' && (
                      <span data-testid={`report-approved-${r.id}`} className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold px-2 py-1 bg-emerald-50 rounded">
                        <Check className="w-3 h-3" />Approved{r.approved_by_name ? ` by ${r.approved_by_name}` : ''}
                      </span>
                    )}
                    {r.status === 'rejected' && (
                      <span data-testid={`report-rejected-${r.id}`} className="inline-flex items-center gap-1 text-xs text-rose-700 font-semibold px-2 py-1 bg-rose-50 rounded">
                        <X className="w-3 h-3" />Rejected{r.rejected_by_name ? ` by ${r.rejected_by_name}` : ''}{r.rejection_notes ? ` — ${r.rejection_notes}` : ''}
                      </span>
                    )}
                    {!r.tenant_signature && r.complaint_id && (
                      <Button data-testid={`sign-report-${r.id}`} onClick={() => setSignFor(r)} size="sm" variant="outline" title="Capture the tenant's signature — required before engineer can close"><PenLine className="w-4 h-4 mr-1" />Tenant Sign</Button>
                    )}
                    {r.tenant_signature && (
                      <span className="inline-flex items-center gap-1 text-xs text-green-700 font-semibold px-2 py-1 bg-green-50 rounded">
                        <Check className="w-3 h-3" />Signed by {r.tenant_signed_by}
                      </span>
                    )}
                  </div>
                </Card>
              );
            })}
            {items.length === 0 && <Card className="p-10 text-center"><FileBarChart className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-muted-foreground">No reports yet</p></Card>}
          </div>
        )}

        {/* Feb-2026 — Force-Close modal (tenant unreachable). Calls the same
            /api/complaints/{id}/force-close endpoint used elsewhere so audit
            trail is unified. */}
        {forceCloseFor && (
          <Dialog open onOpenChange={(v) => { if (!v) { setForceCloseFor(null); setForceCloseReason(''); setForceCloseMethod('phone'); } }}>
            <DialogContent data-testid="sr-force-close-dialog" className="max-w-md">
              <DialogHeader><DialogTitle className="flex items-center gap-2 text-rose-700"><AlertTriangle className="w-5 h-5" />Force Close Complaint</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="text-xs text-slate-600 dark:text-slate-300">
                  Report <span className="font-mono">SR-{forceCloseFor.id.slice(0,8)}</span> — will bypass tenant signature and close the linked complaint.
                </div>
                <div>
                  <Label className="text-xs">Confirmed with tenant via</Label>
                  <Select value={forceCloseMethod} onValueChange={setForceCloseMethod}>
                    <SelectTrigger data-testid="sr-fc-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone">📞 Phone call</SelectItem>
                      <SelectItem value="whatsapp">💬 WhatsApp</SelectItem>
                      <SelectItem value="email">✉️ Email</SelectItem>
                      <SelectItem value="in_person">🤝 In person</SelectItem>
                      <SelectItem value="sms">📱 SMS</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Reason / conversation summary <span className="text-rose-600">*</span></Label>
                  <Textarea data-testid="sr-fc-reason" rows={4} placeholder="e.g. Called tenant at 14:20 — confirmed AC working, agrees to close." value={forceCloseReason} onChange={(e) => setForceCloseReason(e.target.value)} />
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="outline" onClick={() => { setForceCloseFor(null); setForceCloseReason(''); }}>Cancel</Button>
                  <Button
                    data-testid="sr-fc-submit"
                    disabled={!forceCloseReason.trim()}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/complaints/${forceCloseFor.complaint_id}/force-close`, { reason: forceCloseReason.trim(), contact_method: forceCloseMethod }, { withCredentials: true });
                        toast.success('Force-closed — reason logged in audit + complaint thread');
                        setForceCloseFor(null); setForceCloseReason(''); setForceCloseMethod('phone');
                        fetchData();
                      } catch (e) { toast.error(e.response?.data?.detail || 'Force-close failed'); }
                    }}
                  >
                    <AlertTriangle className="w-4 h-4 mr-1" />Force Close
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Sign Dialog */}
        {signFor && (
          <Dialog open={!!signFor} onOpenChange={() => { setSignFor(null); setSignature(''); }}>
            <DialogContent>
              <DialogHeader><DialogTitle>Sign Service Report</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div className="p-3 bg-slate-50 rounded-lg text-sm">
                  <div className="font-semibold">{signFor.summary}</div>
                  <div className="text-xs text-muted-foreground">By {signFor.technician_name} · {signFor.time_spent || 0}h</div>
                </div>
                <Label>Type the tenant&apos;s full name as their signature</Label>
                <Input data-testid="sign-input" value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Tenant name" />
                <p className="text-xs text-muted-foreground">By signing, the tenant confirms the work was completed satisfactorily. The engineer can then close the complaint.</p>
                <div className="p-2 rounded bg-teal-50 border border-teal-200 text-[11px] text-teal-800">
                  💡 Alternative — copy this link and send to the tenant so they can sign on their own device:
                  <code className="block mt-1 text-[10px] break-all bg-white p-1 rounded">{window.location.origin}/tenant-sign/{signFor.id}</code>
                </div>
                <Button data-testid="confirm-sign-btn" onClick={sign} disabled={!signature} className="w-full bg-green-600 hover:bg-green-700"><Check className="w-4 h-4 mr-1" />Confirm Signature</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Phase 38 — Approve Materials Dialog:
            Engineer decides Returnable / Consumable per line before approving. */}
        {approveDialogFor && (
          <Dialog open={!!approveDialogFor} onOpenChange={(o) => { if (!o) { setApproveDialogFor(null); setReturnableChoices({}); } }}>
            <DialogContent className="w-[95vw] max-w-2xl">
              <DialogHeader>
                <DialogTitle>Review Material Request</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div className="p-3 bg-slate-50 rounded-lg text-sm">
                  <div className="font-semibold">{approveDialogFor.summary}</div>
                  <div className="text-xs text-muted-foreground">
                    Requested by {approveDialogFor.technician_name}
                    {approveDialogFor.complaint_ticket_number && (
                      <> · complaint <code className="ml-1 px-1 rounded bg-amber-500/10 text-amber-800">{approveDialogFor.complaint_ticket_number}</code></>
                    )}
                  </div>
                </div>
                <p className="text-[12px] text-slate-600">
                  Mark each item as <strong>Returnable</strong> (tools / equipment to be brought back after use) or
                  <strong> Consumable</strong> (parts that will be permanently fitted). Storekeeper only follows up on the returnables.
                </p>
                <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
                  {(approveDialogFor.materials_needed || []).map((m, i) => {
                    if (m.status !== 'pending') return null;
                    const ticket = approveDialogFor.complaint_ticket_number || complaints.find(c => c.id === approveDialogFor.complaint_id)?.ticket_number;
                    const mrq = materialRequestNo(ticket, i);
                    return (
                      <div key={i} data-testid={`approve-mat-line-${i}`} className="border rounded-lg p-3 bg-white">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <code className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-amber-200">{mrq}</code>
                          <span className="text-sm font-semibold">{m.item}</span>
                          <span className="text-xs text-slate-500">— {m.qty} {m.unit}{m.brand ? ` · ${m.brand}` : ''}{m.size ? ` · ${m.size}` : ''}</span>
                        </div>
                        <div className="flex gap-2">
                          <label className={`flex-1 cursor-pointer flex items-center gap-2 p-2 rounded border-2 transition ${!returnableChoices[i] ? 'border-slate-400 bg-slate-50' : 'border-transparent bg-white'}`}>
                            <input
                              type="radio"
                              name={`returnable-${i}`}
                              data-testid={`approve-mat-consumable-${i}`}
                              checked={!returnableChoices[i]}
                              onChange={() => setReturnableChoices({ ...returnableChoices, [i]: false })}
                            />
                            <span className="text-sm">🗑 <strong>Consumable</strong><br /><span className="text-[11px] text-slate-500">Fitted / used up · no return</span></span>
                          </label>
                          <label className={`flex-1 cursor-pointer flex items-center gap-2 p-2 rounded border-2 transition ${returnableChoices[i] ? 'border-cyan-500 bg-cyan-50' : 'border-transparent bg-white'}`}>
                            <input
                              type="radio"
                              name={`returnable-${i}`}
                              data-testid={`approve-mat-returnable-${i}`}
                              checked={!!returnableChoices[i]}
                              onChange={() => setReturnableChoices({ ...returnableChoices, [i]: true })}
                            />
                            <span className="text-sm">🔄 <strong>Returnable</strong><br /><span className="text-[11px] text-slate-500">Tool / equipment · must be returned</span></span>
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-2 pt-2 border-t">
                  <Button data-testid="confirm-approve-mat-btn" onClick={() => approveMat(approveDialogFor.id, returnableChoices)} className="flex-1 bg-green-600 hover:bg-green-700">
                    <Check className="w-4 h-4 mr-1" />Approve all items
                  </Button>
                  <Button variant="outline" onClick={() => { setApproveDialogFor(null); setReturnableChoices({}); }}>Cancel</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Read-only view dialog — everyone with list access can inspect a report */}
        {viewing && (() => {
          // Feb-2026 field-name compat: the SR model has legacy dual-name
          // fields (title/summary, description/details, sla_actual_hours/
          // time_spent). Auto-generated SRs (from WO completion) sometimes
          // only populate one variant. Read both and prefer the non-empty.
          const displaySummary = viewing.summary || viewing.title || '(no summary)';
          const displayDetails = viewing.details || viewing.description || '';
          const displayHours = viewing.time_spent ?? viewing.sla_actual_hours ?? 0;
          const beforePhotos = viewing.before_photos || [];
          const afterPhotos = viewing.after_photos || [];
          const otherPhotos = (viewing.photos || []).filter(p => !beforePhotos.includes(p) && !afterPhotos.includes(p));
          const signatureUrl = viewing.tenant_signature_url || viewing.tenant_signature;
          const backendOrigin = API.replace('/api', '');
          const resolveUrl = (u) => (u && u.startsWith('http')) ? u : `${backendOrigin}${u}`;
          return (
          <Dialog open onOpenChange={() => setViewing(null)}>
            <DialogContent className="w-[98vw] sm:w-[95vw] max-w-7xl max-h-[95vh] overflow-y-auto" data-testid="view-report-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  <FileBarChart className="w-5 h-5 text-teal-600" />
                  {displaySummary}
                  <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-mono">{REPORT_STATUS_LABEL[viewing.status] || viewing.status}</span>
                  {viewing.sla_met === true && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">SLA MET</span>}
                  {viewing.sla_met === false && <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 font-semibold">SLA BREACHED</span>}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <ViewField label="Building" value={buildings.find(b => b.id === viewing.building_id)?.name || '—'} />
                  <ViewField label="Technician" value={viewing.technician_name || '—'} />
                  <ViewField label="Created" value={new Date(viewing.created_at).toLocaleString()} />
                  <ViewField label="Time consumed" value={`${displayHours}h${viewing.sla_target_hours ? ` (target: ${viewing.sla_target_hours}h)` : ''}`} />
                  {viewing.complaint_ticket_number && <ViewField label="Ticket" value={viewing.complaint_ticket_number} />}
                  {viewing.work_order_number && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Work Order</div>
                      <a href={`/work-orders?highlight=${viewing.work_order_id}`} className="text-sm font-mono text-blue-700 hover:underline" data-testid="view-wo-link">{viewing.work_order_number}</a>
                    </div>
                  )}
                  {viewing.approved_by_name && <ViewField label="Approved by" value={`${viewing.approved_by_name} · ${viewing.approved_at ? new Date(viewing.approved_at).toLocaleString() : ''}`} />}
                  {viewing.tenant_signed_by && <ViewField label="Tenant signed" value={`${viewing.tenant_signed_by} · ${viewing.tenant_signed_at ? new Date(viewing.tenant_signed_at).toLocaleString() : ''}`} />}
                  {viewing.updated_by_name && <ViewField label="Last edited" value={`${viewing.updated_by_name} · ${new Date(viewing.updated_at).toLocaleString()}`} />}
                </div>
                {displayDetails && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Action taken / Remarks by technician</div>
                    <p className="whitespace-pre-wrap text-slate-700 text-sm mt-1 p-3 bg-slate-50 rounded border border-slate-200" data-testid="view-work-details">{displayDetails}</p>
                  </div>
                )}
                {viewing.materials_needed?.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold mb-1">Materials Requested</div>
                    <ul className="text-xs space-y-1">
                      {viewing.materials_needed.map((m, i) => (
                        <li key={i} className="p-2 rounded bg-amber-50 border border-amber-200 flex flex-wrap gap-2">
                          <code className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-amber-200">{materialRequestNo(viewing.complaint_ticket_number, i)}</code>
                          <span className="flex-1">📦 <strong>{m.item}</strong>{m.brand ? ` · ${m.brand}` : ''}{m.size ? ` · ${m.size}` : ''} — {m.qty} {m.unit}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-semibold">{m.status || 'pending'}</span>
                          {m.is_returnable && <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-800">🔄 Returnable</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {viewing.materials_used?.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-600 font-semibold mb-1">Materials Used</div>
                    <ul className="text-xs space-y-0.5 pl-4 list-disc" data-testid="view-materials-used">
                      {viewing.materials_used.map((m, i) => <li key={i}><strong>{m.item}</strong> — {m.qty} {m.unit || ''}{m.brand ? ` · ${m.brand}` : ''}</li>)}
                    </ul>
                  </div>
                )}
                {viewing.rejection_notes && (
                  <div className="p-2 rounded bg-rose-50 border border-rose-200 text-rose-900 text-xs">
                    <strong>Rejection notes:</strong> {viewing.rejection_notes}
                  </div>
                )}
                {(beforePhotos.length > 0 || afterPhotos.length > 0) && (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {beforePhotos.length > 0 && (
                      <div>
                        <div className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold mb-1">📷 Before ({beforePhotos.length})</div>
                        <div className="flex flex-wrap gap-2" data-testid="view-before-photos">
                          {beforePhotos.map((url, i) => (
                            <a key={i} href={resolveUrl(url)} target="_blank" rel="noopener noreferrer" className="block">
                              <img src={resolveUrl(url)} alt={`Before ${i + 1}`} className="w-20 h-20 object-cover rounded border-2 border-amber-300 hover:ring-2 hover:ring-amber-400 transition" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    {afterPhotos.length > 0 && (
                      <div>
                        <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold mb-1">✅ After ({afterPhotos.length})</div>
                        <div className="flex flex-wrap gap-2" data-testid="view-after-photos">
                          {afterPhotos.map((url, i) => (
                            <a key={i} href={resolveUrl(url)} target="_blank" rel="noopener noreferrer" className="block">
                              <img src={resolveUrl(url)} alt={`After ${i + 1}`} className="w-20 h-20 object-cover rounded border-2 border-emerald-300 hover:ring-2 hover:ring-emerald-400 transition" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {otherPhotos.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Photos ({otherPhotos.length})</div>
                    <div className="flex flex-wrap gap-2">
                      {otherPhotos.map((url, i) => (
                        <a key={i} href={resolveUrl(url)} target="_blank" rel="noopener noreferrer" className="block">
                          <img src={resolveUrl(url)} alt={`Photo ${i + 1}`} className="w-20 h-20 object-cover rounded border hover:ring-2 hover:ring-teal-400 transition" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {signatureUrl && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">✍️ Tenant Signature</div>
                    {signatureUrl.startsWith('signed_by:') ? (
                      <div className="p-2 rounded bg-slate-50 border border-slate-200 text-sm text-slate-700" data-testid="view-tenant-signature">
                        Signed by <strong>{signatureUrl.replace('signed_by:', '')}</strong>
                      </div>
                    ) : (
                      <img src={resolveUrl(signatureUrl)} alt="Tenant signature" className="max-h-24 border border-slate-200 rounded bg-white p-1" data-testid="view-tenant-signature-img" />
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-2 justify-end pt-3 border-t mt-2">
                {/* Read-only for engineers/managers/admins. Only the author-
                    technician can edit their OWN draft SR before submission —
                    matches the row-level rule (line ~644). */}
                {viewing.technician_id === user?.id && ['draft', 'completed'].includes(viewing.status) && (
                  <Button data-testid="view-edit-btn" variant="outline" onClick={() => { openEditReport(viewing); setViewing(null); }}>
                    <PenLine className="w-4 h-4 mr-1" />Edit
                  </Button>
                )}
                <Button data-testid="view-pdf-btn" variant="outline" onClick={() => downloadReportPDF(viewing)}>
                  <Download className="w-4 h-4 mr-1" />PDF
                </Button>
                <Button onClick={() => setViewing(null)} className="bg-teal-600 hover:bg-teal-700">Close</Button>
              </div>
            </DialogContent>
          </Dialog>
          );
        })()}
      </div>
    </Layout>
  );
}

function ViewField({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-800">{value}</div>
    </div>
  );
}
